/**
 * Data access for the BYOC feature.
 *
 * Talks to the Keystone AppSync API (KeystoneStack). Reads are served straight
 * from RDS over the Data API — no Lambda in the path — and writes go through
 * the Keystone resolver Lambda. Every operation takes a single JSON payload
 * (`input: AWSJSON`) and returns JSON, so the shapes below mirror what the
 * resolvers produce.
 *
 * Fixtures remain as a fallback for stages where the stack has not been
 * deployed yet (the endpoint is empty in KEYSTONE_GRAPHQL_CONFIG until its
 * first deploy). That keeps the page usable during rollout rather than showing
 * an error for infrastructure the user cannot do anything about — and
 * `USING_FIXTURES` tells the caller which it got, so a developer is never
 * fooled into thinking fixture rows are real data.
 */

import { callKeystoneGraphQLDirect, getKeystoneAppSyncConfig } from "src/utilities/AWSServices";
import { uploadDocumentToVault } from "src/Ateam-BI/Vault/VaultAPIs";
import { VAULT_SERVICES } from "src/Ateam-BI/Vault/constants";
import { LIFECYCLE_STATUS, PAYLOAD_FAMILY, SOLUTION_STATUS } from "./constants";

/* ---- GraphQL documents ---- */

const LIST_CODE_INFO = `
  query ListCodeInfo($input: AWSJSON) {
    listCodeInfo(input: $input)
  }
`;

const GET_CODE_INFO = `
  query GetCodeInfo($input: AWSJSON!) {
    getCodeInfo(input: $input)
  }
`;

const CHECK_CODE_NAME = `
  query CheckCodeNameAvailable($input: AWSJSON!) {
    checkCodeNameAvailable(input: $input)
  }
`;

const CREATE_CODE_INFO = `
  mutation CreateCodeInfo($input: AWSJSON!) {
    createCodeInfo(input: $input)
  }
`;

const UPDATE_CODE_INFO = `
  mutation UpdateCodeInfo($input: AWSJSON!) {
    updateCodeInfo(input: $input)
  }
`;

const SET_CODE_STATUS = `
  mutation SetCodeStatus($input: AWSJSON!) {
    setCodeStatus(input: $input)
  }
`;

const RECORD_TRANSACTION = `
  mutation RecordTransaction($input: AWSJSON!) {
    recordTransaction(input: $input)
  }
`;

const CODE_SUBMIT = `
  mutation CodeSubmit($input: AWSJSON!) {
    codeSubmit(input: $input)
  }
`;

const GET_TRANSACTION = `
  query GetTransaction($input: AWSJSON!) {
    getTransaction(input: $input)
  }
`;

const LIST_TRANSACTIONS = `
  query ListTransactions($input: AWSJSON!) {
    listTransactions(input: $input)
  }
`;

/**
 * AppSync returns AWSJSON as a JSON string, and the response VTL can hand back
 * a payload the resolver already serialized — so it can arrive double-encoded.
 * Parse repeatedly until a non-string falls out, tolerating either depth.
 */
const parseResult = (response, field) => {
  let raw = response && response.data ? response.data[field] : null;
  if (raw == null) return null;
  while (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch (e) {
      break; // A plain string value — return as-is.
    }
  }
  return raw;
};

/** A resolver may answer with { error, code } rather than throwing. */
const throwIfError = (payload) => {
  if (payload && payload.error) {
    const error = new Error(payload.error);
    error.code = payload.code;
    throw error;
  }
  return payload;
};

const call = async (query, field, input) => {
  const response = await callKeystoneGraphQLDirect(query, {
    input: JSON.stringify(input || {}),
  });
  return throwIfError(parseResult(response, field));
};

/* ---- Mapping registry rows onto what the components read ----
 *
 * The registry's `status` is the coarse lifecycle (WIP / Active / Deprecated).
 * The UI also needs to know *where* a WIP solution is — running checks, held,
 * or awaiting review — which the registry does not store, so it is derived.
 * Keeping the derivation here means the components read one consistent shape
 * whether the data came from the API or a fixture.
 */
const derivePipelineState = (row) => {
  if (row.status === LIFECYCLE_STATUS.ACTIVE) {
    return row.byoc_link ? SOLUTION_STATUS.LIVE : SOLUTION_STATUS.PROCESSING;
  }
  if (row.status === LIFECYCLE_STATUS.DEPRECATED) {
    return SOLUTION_STATUS.LIVE;
  }
  // WIP. A script sits in review; anything else is still being processed.
  return row.byoc_type === PAYLOAD_FAMILY.SCRIPT
    ? SOLUTION_STATUS.IN_REVIEW
    : SOLUTION_STATUS.PROCESSING;
};

const FAMILY_BY_BYOC_TYPE = {
  HTML: PAYLOAD_FAMILY.STATIC_UI,
  STREAMLIT: PAYLOAD_FAMILY.SERVER_UI,
  FLASK: PAYLOAD_FAMILY.SERVER_UI,
  SCRIPT: PAYLOAD_FAMILY.SCRIPT,
};

/**
 * PENDING is the honest state between registration and classification, and
 * UNKNOWN is what an unidentifiable archive gets. Neither is a family, so the
 * list shows the raw type rather than mislabelling the row as an HTML app —
 * which is exactly what the old UI-side guess did.
 */
const UNCLASSIFIED_TYPES = ["PENDING", "UNKNOWN"];
export const isUnclassified = (byocType) => UNCLASSIFIED_TYPES.includes(byocType);

const toSolution = (row) => {
  if (!row) return null;
  const linkages = row.linkages || [];
  return {
    id: row.code_ref_id,
    name: row.code_name,
    family: FAMILY_BY_BYOC_TYPE[row.byoc_type] || null,
    byocType: row.byoc_type,
    status: row.status,
    pipelineState: derivePipelineState(row),
    link: row.byoc_link || null,
    owner: row.created_by,
    updatedAt: row.last_updated_timestamp || row.created_timestamp,
    updatedBy: row.last_updated_by || row.created_by,
    createdAt: row.created_timestamp,
    linkages,
    // Dynamic data is modelled as a linkage rather than a column, so an app can
    // carry several and the registry needs no schema change per kind.
    dynamicDataEnabled: linkages.some((linkage) => linkage.linkage_type === "DYNAMIC_DATA"),
  };
};

/* ---- Fixture fallback ---- */

const FIXTURE_SOLUTIONS = [
  {
    code_ref_id: "4f2a9c1e",
    code_name: "sales-report",
    byoc_type: "HTML",
    status: LIFECYCLE_STATUS.ACTIVE,
    byoc_link: "https://topos2.ai-apps.beta.bi-central.accounting.amazon.dev/sales-report/index.html",
    created_by: "ravsbhar",
    created_timestamp: "2026-10-01T09:00:00Z",
    last_updated_by: "ravsbhar",
    last_updated_timestamp: "2026-10-11T09:14:00Z",
    linkages: [{ link: "bi-pipeline", linkage_type: "DYNAMIC_DATA", created_timestamp: "2026-10-11T09:14:00Z" }],
  },
  {
    code_ref_id: "7b1e40a2",
    code_name: "accrual-calc",
    byoc_type: "SCRIPT",
    status: LIFECYCLE_STATUS.WIP,
    byoc_link: null,
    created_by: "ravsbhar",
    created_timestamp: "2026-10-12T13:22:00Z",
    last_updated_by: "ravsbhar",
    last_updated_timestamp: "2026-10-12T13:22:00Z",
    linkages: [],
  },
];

let endpointConfigured = null;

/**
 * True when the Keystone stack has an endpoint for this stage. Cached after the
 * first check so the page does not re-resolve it per call.
 */
export const isApiConfigured = async () => {
  if (endpointConfigured !== null) return endpointConfigured;
  try {
    const config = await getKeystoneAppSyncConfig();
    endpointConfigured = Boolean(config && config.Appsync && config.Appsync.aws_appsync_graphqlEndpoint);
  } catch (error) {
    endpointConfigured = false;
  }
  return endpointConfigured;
};

/** Which source the last read came from. Exported so a caller can surface it. */
export let USING_FIXTURES = false;

/* ---- Reads (direct RDS over the Data API) ---- */

export const listSolutions = async (filter = {}) => {
  if (!(await isApiConfigured())) {
    USING_FIXTURES = true;
    return FIXTURE_SOLUTIONS.map(toSolution);
  }
  USING_FIXTURES = false;
  const payload = await call(LIST_CODE_INFO, "listCodeInfo", filter);
  return ((payload && payload.code_info) || []).map(toSolution);
};

export const getSolution = async (idOrName) => {
  if (!(await isApiConfigured())) {
    USING_FIXTURES = true;
    const match = FIXTURE_SOLUTIONS.find(
      (row) => row.code_ref_id === idOrName || row.code_name === idOrName
    );
    return toSolution(match);
  }
  USING_FIXTURES = false;
  // An id is a uuid; anything else is treated as a name.
  const input = /^[0-9a-f-]{8,36}$/i.test(idOrName)
    ? { code_ref_id: idOrName }
    : { code_name: idOrName };
  const payload = await call(GET_CODE_INFO, "getCodeInfo", input);
  return toSolution(payload && payload.code_info);
};

/**
 * Server-side name availability, for the as-you-type check.
 *
 * The UI validates the name's *shape* locally (see validation.js) because that
 * needs no round trip. Only availability needs the server, and this query is
 * wired to the Data API precisely because it is called on a keystroke cadence.
 */
export const checkNameAvailable = async (codeName) => {
  if (!(await isApiConfigured())) {
    return {
      available: !FIXTURE_SOLUTIONS.some((row) => row.code_name === codeName),
      taken_by: null,
      near_miss: null,
    };
  }
  return call(CHECK_CODE_NAME, "checkCodeNameAvailable", { code_name: codeName });
};

/* ---- Writes (Keystone resolver Lambda) ---- */

/**
 * Onboard or update a solution, end to end.
 *
 * Four steps, in this order, because each depends on the last:
 *
 *   1. registry row    — so the solution has a code_ref_id before anything else
 *   2. upload the zip  — straight to S3 with a presigned PUT, not through
 *                        AppSync, which caps an invocation payload at 6 MB
 *   3. transaction row — the audit record, written BEFORE publishing so an
 *                        upload that fails to publish is still tracked
 *   4. codeSubmit      — unzip into the published prefix, invalidate the cache,
 *                        and mark the transaction done with the URL
 *
 * Returns as soon as step 4 is accepted. The caller polls, or waits for the
 * email the stream listener sends when the deploy reaches a terminal status.
 *
 * `onProgress` is called with a stage key before each step, so the UI can say
 * which of the four is running rather than showing one long spinner.
 */
export const submitUpload = async (file, config = {}, onProgress = () => {}) => {
  const isUpdate = Boolean(config.updatesSolutionId);

  // --- 1. registry row ---
  onProgress("registering");
  let solution;
  if (isUpdate) {
    const payload = await call(UPDATE_CODE_INFO, "updateCodeInfo", {
      code_ref_id: config.updatesSolutionId,
    });
    solution = toSolution(payload && payload.code_info);
  } else {
    // No byoc_type: the browser has only seen a filename, and a filename
    // cannot tell an HTML report from a Streamlit app — both are .zip.
    // codeSubmit inspects the archive and records the real type, so asserting
    // one here would put a guess in the registry.
    const payload = await call(CREATE_CODE_INFO, "createCodeInfo", {
      code_name: config.name,
    });
    solution = toSolution(payload && payload.code_info);
  }
  if (!solution) throw new Error("The registry did not return the solution");

  // --- 2. transaction row, BEFORE the upload ---
  // Its id is also the archive's S3 key, so it has to exist before the file
  // is written. Recording it first also means an upload that then fails is
  // already a tracked event rather than an untraceable gap.
  onProgress("recording");
  const transaction = await call(RECORD_TRANSACTION, "recordTransaction", {
    code_ref_id: solution.id,
    code_name: solution.name,
    is_update: isUpdate,
  });
  const transactionId = transaction && transaction.transaction_id;
  if (!transactionId) throw new Error("Could not record the upload");

  // --- 3. upload the archive through Vault ---
  // Vault owns every browser-to-S3 upload in the portal: it presigns the PUT
  // and performs it, and its SERVICE_PREFIX_MAP confines KEYSTONE to
  // apps/uploads/. One object per upload, keyed by the upload's own id — so a
  // re-upload never overwrites the previous archive and the history is real
  // rather than reconstructed.
  //
  // apps/uploads/ sits OUTSIDE the CloudFront OriginPath (/apps/live), so the
  // raw source archives are not fetchable from the web even though they share
  // a bucket with the published apps. Writing to apps/live/ from a browser is
  // refused by Vault, which is what stops a published app being overwritten
  // without going through codeSubmit.
  onProgress("uploading");
  try {
    await uploadDocumentToVault(
      "zip",
      VAULT_SERVICES.KEYSTONE,
      { file_path: `apps/uploads/${transactionId}` },
      file
    );
  } catch (error) {
    // A failed upload must stop here. Publishing now would deploy whatever
    // happened to be at that key — or nothing at all.
    const detail = error && error.message ? error.message : "upload failed";
    throw new Error(`Upload failed: ${detail}`);
  }

  // --- 4. start publishing ---
  // This returns as soon as the work is handed off, NOT when it finishes.
  // AppSync caps a request at 30 seconds while publishing is allowed 15
  // minutes, so a large upload would otherwise blow the mutation while the
  // Lambda carried on — showing the user a failure for work that succeeded.
  //
  // Progress lands on the transaction item; poll it with pollDeploy below, or
  // wait for the email the stream listener sends on a terminal status.
  onProgress("publishing");
  const accepted = await call(CODE_SUBMIT, "codeSubmit", {
    code_ref_id: solution.id,
    code_name: solution.name,
    transaction_id: transactionId,
    file_id: transactionId,
  });

  return {
    ...solution,
    transactionId,
    accepted: Boolean(accepted && accepted.accepted),
    deployStatus: (accepted && accepted.deploy_status) || "pending",
  };
};

/** Terminal deploy states — polling stops at either. */
export const TERMINAL_DEPLOY_STATUSES = ["done", "failed"];

/**
 * Read the transaction item once.
 *
 * Returns null when the item is not readable yet, which happens briefly right
 * after the write — the caller should treat that as "keep waiting", not as a
 * failure.
 */
export const getDeployStatus = async (codeRefId, transactionId) => {
  const payload = await call(GET_TRANSACTION, "getTransaction", {
    code_ref_id: codeRefId,
    transaction_id: transactionId,
  });
  return (payload && payload.transaction) || null;
};

/**
 * Poll until the publish reaches a terminal state, or the attempts run out.
 *
 * `onTick` is called with each transaction item so the UI can show real
 * progress — data_copied, cloudfront_invalidated, deploy_status — rather than
 * a spinner that means nothing.
 *
 * Running out of attempts is NOT reported as a failure: the publish may still
 * be running, and the email will arrive either way. Saying "failed" here would
 * be a guess about work we simply stopped watching.
 */
export const pollDeploy = async (
  codeRefId,
  transactionId,
  { intervalMs = 3000, maxAttempts = 100, onTick = () => {} } = {}
) => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    const item = await getDeployStatus(codeRefId, transactionId);
    if (item) {
      onTick(item);
      if (TERMINAL_DEPLOY_STATUSES.includes(item.deploy_status)) {
        return item;
      }
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return { deploy_status: "pending", timed_out: true };
};

export const setSolutionStatus = async (id, status) => {
  const payload = await call(SET_CODE_STATUS, "setCodeStatus", {
    code_ref_id: id,
    status,
  });
  return toSolution(payload && payload.code_info);
};

/* ---- Not yet backed by Keystone ----
 *
 * These belong to the upload pipeline (unpack, classify, host, notify) and the
 * dynamic-data ticket flow, none of which exist yet. They are kept with their
 * real signatures so the components do not change when the backend lands.
 */

/**
 * A hint, not a decision.
 *
 * Used ONLY to choose which interview questions to show — a .py upload clearly
 * wants the script questions, a .zip is ambiguous. The authoritative type is
 * decided by codeSubmit from the archive's contents and written to the
 * registry there; nothing in this file sends a type to the backend.
 */
export const detectFamily = async (file) => {
  if (!file || !file.name) return null;
  return file.name.toLowerCase().endsWith(".py")
    ? PAYLOAD_FAMILY.SCRIPT
    : PAYLOAD_FAMILY.STATIC_UI;
};

/**
 * Stage list for the status panel, derived from the transaction item.
 *
 * The publish is asynchronous, so this reads what codeSubmit has recorded so
 * far rather than guessing: data_copied and cloudfront_invalidated are written
 * as each step completes, which is what makes "copied but not invalidated"
 * distinguishable from "never copied".
 */
export const getSubmissionStatus = async (codeRefId, transactionId) => {
  let item = null;
  if (codeRefId) {
    try {
      // Falls back to the newest transaction when no id is given.
      //
      // The registry row carries no transaction id — it is current state, and
      // the ids live on the transaction log — so a solution opened from the
      // LIST has none to pass. Requiring one meant every in-review or held
      // solution showed all stages pending and no findings: precisely the ones
      // a user opens this panel to understand.
      let id = transactionId;
      if (!id) {
        const uploads = await listUploads(codeRefId, 1);
        id = uploads && uploads.length ? uploads[0].transaction_id : null;
      }
      item = id ? await getDeployStatus(codeRefId, id) : null;
    } catch (error) {
      item = null;
    }
  }

  const status = (item && item.deploy_status) || "pending";
  const copied = Boolean(item && item.data_copied);
  const invalidated = Boolean(item && item.cloudfront_invalidated);
  const failed = status === "failed";

  const stage = (done, active) => (done ? "done" : active ? "active" : "pending");

  return {
    id: codeRefId,
    pipelineState:
      status === "done" ? SOLUTION_STATUS.LIVE : SOLUTION_STATUS.PROCESSING,
    deployStatus: status,
    stages: [
      { key: "received", state: "done", detail: null },
      {
        key: "registered",
        state: "done",
        detail: transactionId ? `id ${codeRefId} — emailed to you` : null,
      },
      {
        key: "scanned",
        state: failed && !copied ? "failed" : stage(copied, status === "in_progress"),
        detail: item && item.byoc_type ? `detected ${item.byoc_type}` : null,
      },
      {
        key: "detected",
        state: stage(Boolean(item && item.byoc_type), false),
        detail: (item && item.classification_reason) || null,
      },
      {
        key: "published",
        state: failed && copied && !invalidated ? "failed" : stage(invalidated, copied),
        detail:
          item && item.objects_written ? `${item.objects_written} files` : null,
      },
      {
        key: "notified",
        state: stage(status === "done", invalidated),
        detail: (item && item.deploy_url) || null,
      },
    ],
    findings: failed && item && item.error ? [{ detail: item.error }] : [],
  };
};

export const setDynamicData = async (id, enabled) => ({
  id,
  dynamicDataEnabled: enabled,
  appName: null,
  refreshLambdaName: enabled ? "abc-byoc-refresh-assets-beta" : null,
  biTicket: enabled ? { id: "TT-pending", url: "#", status: "not yet raised" } : null,
});

export const reassignOwner = async (id, newOwner) => ({
  id,
  owner: newOwner,
  pipelineState: SOLUTION_STATUS.LIVE,
});

/**
 * Every upload recorded against one solution, newest first.
 *
 * This is the upload history. Each entry carries what was submitted
 * (`archive_s3_path`), what it was classified as, how far the publish got
 * (`data_copied`, `cloudfront_invalidated`), and how it ended.
 */
export const listUploads = async (codeRefId, limit = 50) => {
  const payload = await call(LIST_TRANSACTIONS, "listTransactions", {
    code_ref_id: codeRefId,
    limit,
  });
  return (payload && payload.transactions) || [];
};
